/**
 * Display helpers for guest booking inquiry cards (/bookings).
 */

export function getInquiryRecordId(inquiry) {
  if (!inquiry || typeof inquiry !== 'object') {
    return '';
  }
  return String(inquiry.id || inquiry.inquiry_id || '').trim();
}

export function shortId(value, length = 8) {
  if (!value || typeof value !== 'string') {
    return '';
  }
  const text = value.trim();
  if (!text) {
    return '';
  }
  return text.length <= length ? text : text.slice(0, length);
}

export function displayText(value, fallback) {
  if (value === null || value === undefined) {
    return fallback;
  }
  const text = String(value).trim();
  return text || fallback;
}

export function formatInquiryDate(dateStr) {
  if (!dateStr) {
    return null;
  }
  const raw = String(dateStr).trim();
  const parsed = new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateTime(isoStr) {
  if (!isoStr) {
    return null;
  }
  const parsed = new Date(isoStr);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function computeNights(inquiry) {
  if (typeof inquiry?.nights === 'number' && inquiry.nights > 0) {
    return inquiry.nights;
  }
  const checkIn = inquiry?.check_in_date;
  const checkOut = inquiry?.check_out_date;
  if (!checkIn || !checkOut) {
    return null;
  }
  const start = new Date(String(checkIn).includes('T') ? checkIn : `${checkIn}T12:00:00`);
  const end = new Date(String(checkOut).includes('T') ? checkOut : `${checkOut}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return null;
  }
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end - start) / msPerDay);
}

export function formatDurationLabel(inquiry, t) {
  const nights = computeNights(inquiry);
  if (nights === null) {
    return t('booking.duration_unavailable', 'Duration unavailable');
  }
  return t('booking.nights_count', '{count} nights').replace('{count}', String(nights));
}

export function formatEstimatedTotal(inquiry) {
  const amount = inquiry?.estimated_price;
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return null;
  }
  const currency = (inquiry?.listing_currency || 'EUR').toUpperCase();
  const symbol = currency === 'EUR' ? '€' : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

const VIEWING_STATE_KEYS = {
  no_viewing: 'booking.viewing_none',
  viewing_offered: 'booking.viewing_offered',
  viewing_reserved: 'booking.viewing_reserved',
  viewing_confirmed: 'booking.viewing_confirmed',
};

export function viewingStateLabel(viewingState, t) {
  const key = VIEWING_STATE_KEYS[viewingState] || VIEWING_STATE_KEYS.no_viewing;
  const defaults = {
    'booking.viewing_none': 'No viewing slot selected',
    'booking.viewing_offered': 'Viewing slots available',
    'booking.viewing_reserved': 'Viewing reserved',
    'booking.viewing_confirmed': 'Viewing confirmed',
  };
  return t(key, defaults[key] || defaults['booking.viewing_none']);
}

export function listingTitle(inquiry, t) {
  return displayText(
    inquiry?.listing_title,
    t('booking.unknown_listing', 'Unknown listing'),
  );
}

export function listingLocation(inquiry, t) {
  const location = inquiry?.listing_location || inquiry?.listing_city;
  return displayText(location, t('booking.location_unavailable', 'Location unavailable'));
}

const INQUIRY_STATUS_BADGE_CONFIGS = {
  pending: {
    labelKey: 'booking.inquiry_status_pending',
    labelFallback: 'Awaiting owner response',
    badgeClassName: 'bg-yellow-500/20 text-yellow-700 border-yellow-300',
  },
  approved: {
    labelKey: 'booking.inquiry_status_approved',
    labelFallback: 'Approved',
    badgeClassName: 'bg-emerald-500/20 text-emerald-700 border-emerald-300',
  },
  confirmed: {
    labelKey: 'booking.inquiry_status_confirmed',
    labelFallback: 'Confirmed',
    badgeClassName: 'bg-signal-blue/20 text-signal-blue border-signal-blue/30',
  },
  declined: {
    labelKey: 'booking.inquiry_status_declined',
    labelFallback: 'Declined',
    badgeClassName: 'bg-red-500/20 text-red-700 border-red-300',
  },
  cancelled: {
    labelKey: 'booking.inquiry_status_cancelled',
    labelFallback: 'Withdrawn',
    badgeClassName: 'bg-slate-500/20 text-slate-600 border-slate-300',
  },
  refunded: {
    labelKey: 'booking.inquiry_status_refunded',
    labelFallback: 'Refunded',
    badgeClassName: 'bg-purple-500/20 text-purple-700 border-purple-300',
  },
};

/**
 * Shared inquiry status badge label + classes for guest and admin surfaces.
 */
export function getInquiryStatusBadgeConfig(status, t) {
  const normalized = String(status || '').toLowerCase().trim();
  const config = INQUIRY_STATUS_BADGE_CONFIGS[normalized];
  if (config) {
    return {
      label: t(config.labelKey, config.labelFallback),
      badgeClassName: config.badgeClassName,
    };
  }
  const humanized = normalized
    ? normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : '';
  return {
    label: humanized || t('booking.inquiry_status_closed', 'Closed'),
    badgeClassName: 'bg-slate-500/20 text-slate-600 border-slate-300',
  };
}
